const favorites = []
const MAX_FAVORITES = 10

// Event listeners for reactive updates
const listeners = new Set()

// Export the max constant for external use
export { MAX_FAVORITES }

const loadInitialState = () => {
  try {
    // Migrate from old dual-storage format if needed
    const storedPinned = localStorage.getItem('userPinnedFavorites')
    const stored = localStorage.getItem('userFavorites')

    if (storedPinned) {
      // Old format had separate pinned list — merge into unified favorites
      const parsedPinned = JSON.parse(storedPinned)
      const parsedAuto = stored ? JSON.parse(stored) : []
      const merged = [...parsedPinned]
      parsedAuto.forEach(item => {
        if (!merged.some(m => m.route === item.route)) {
          merged.push(item)
        }
      })
      favorites.length = 0
      merged.slice(0, MAX_FAVORITES).forEach(item => favorites.push({
        name: item.name || item.title,
        title: item.title || item.name,
        route: item.route,
        icon: item.icon || 'StarIcon',
        hasPermission: item.hasPermission || null,
      }))
      // Save in new format and remove old keys
      localStorage.setItem('userFavorites', JSON.stringify(favorites))
      localStorage.removeItem('userPinnedFavorites')
      return
    }

    if (stored) {
      const parsed = JSON.parse(stored)
      favorites.length = 0
      parsed.slice(0, MAX_FAVORITES).forEach(item => favorites.push(item))
    }
  } catch (error) {
    console.error('Error loading favorites:', error)
  }
}

const saveToLocalStorage = () => {
  localStorage.setItem('userFavorites', JSON.stringify(favorites))
}

const notifyListeners = () => {
  listeners.forEach(callback => callback())
}

loadInitialState()

const FavoriteService = {

  /**
   * Get all favorites (order preserved as added)
   */
  getAllFavorites() {
    return [...favorites]
  },

  /**
   * Get favorites filtered by the user's current permissions
   * @param {Array} userPermissions - Array of permission strings the user has
   */
  getPermittedFavorites(userPermissions) {
    if (!userPermissions || !Array.isArray(userPermissions)) {
      return [...favorites]
    }
    return favorites.filter(
      fav => !fav.hasPermission || userPermissions.includes(fav.hasPermission),
    )
  },

  /**
   * Check if a route is in favorites
   */
  isFavorite(routeName) {
    return favorites.some(item => item.route === routeName)
  },

  /**
   * Check if the maximum favorites limit has been reached
   */
  isLimitReached() {
    return favorites.length >= MAX_FAVORITES
  },

  /**
   * Get the current favorites count
   */
  getFavoritesCount() {
    return favorites.length
  },

  /**
   * Add a page to favourites
   */
  addFavorite(menuItem) {
    if (!menuItem || !menuItem.route) return false

    // Already a favourite
    if (this.isFavorite(menuItem.route)) return false

    // Max limit reached
    if (favorites.length >= MAX_FAVORITES) {
      console.warn('Maximum favourites reached')
      return false
    }

    favorites.push({
      name: menuItem.name || menuItem.title,
      title: menuItem.title || menuItem.name,
      route: menuItem.route,
      icon: menuItem.icon || 'StarIcon',
      hasPermission: menuItem.hasPermission || null,
    })

    saveToLocalStorage()
    notifyListeners()
    return true
  },

  /**
   * Remove a page from favourites
   */
  removeFavorite(routeName) {
    const index = favorites.findIndex(item => item.route === routeName)
    if (index >= 0) {
      favorites.splice(index, 1)
      saveToLocalStorage()
      notifyListeners()
    }
  },

  /**
   * Subscribe to changes
   */
  subscribe(callback) {
    listeners.add(callback)
    return () => listeners.delete(callback)
  },

  /**
   * Record a page visit (increments count without reordering)
   */
  recordInteraction(menuItem) {
    if (!menuItem || !menuItem.name || !menuItem.route) return

    const existing = favorites.find(item => item.route === menuItem.route)
    if (existing) {
      existing.count = (existing.count || 0) + 1
      existing.lastAccessed = Date.now()
      saveToLocalStorage()
      notifyListeners()
    }
  },

  /**
   * Alias kept for dashboard widget compatibility
   */
  getFavorites() {
    return [...favorites]
  },

  clearFavorites() {
    favorites.length = 0
    saveToLocalStorage()
    notifyListeners()
  },
}

export default FavoriteService
